import { useMemo, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/dialog';
import { SortHeader, makeToggleSort, cmp, type SortState } from '@/components/ui/sort-header';
import { partiesApi, type Party, type PartyUsage } from '@/api';
import { ApiError } from '@/lib/api';

type PartyType = 'CUSTOMER' | 'SUPPLIER' | 'EMPLOYEE' | 'CONTRACTOR';

const partyTypeOrder: Record<Party['type'], number> = {
  CUSTOMER: 0, SUPPLIER: 1, EMPLOYEE: 2, CONTRACTOR: 3,
};

type PartySortColumn = 'code' | 'name' | 'type' | 'email' | 'phone' | 'active';


function TypeBadge({ type }: { type: Party['type'] }) {
  switch (type) {
    case 'CUSTOMER':   return <Badge variant="success">Cliente</Badge>;
    case 'SUPPLIER':   return <Badge variant="warning">Fornecedor</Badge>;
    case 'EMPLOYEE':   return <Badge variant="info">Funcionário</Badge>;
    case 'CONTRACTOR': return <Badge variant="info">Terceirizado</Badge>;
    default:           return <Badge>{type}</Badge>;
  }
}

export function ContrapartesPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState({ type: '', q: '' });
  const [sort, setSort] = useState<SortState<PartySortColumn>>({ column: 'code', dir: 'asc' });
  const toggleSort = makeToggleSort(setSort);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState<Party | null>(null);
  const [deleting, setDeleting] = useState<Party | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['parties', filter],
    queryFn: () => partiesApi.list({ type: filter.type || undefined, q: filter.q || undefined }),
  });

  const sortedItems = useMemo(() => {
    const rows = data ?? [];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let primary = 0;
      switch (sort.column) {
        case 'code':   primary = cmp.text(a.code, b.code); break;
        case 'name':   primary = cmp.text(a.name, b.name); break;
        case 'type':   primary = partyTypeOrder[a.type] - partyTypeOrder[b.type]; break;
        case 'email':  primary = cmp.text(a.email ?? '', b.email ?? ''); break;
        case 'phone':  primary = cmp.text(a.phone ?? '', b.phone ?? ''); break;
        case 'active': primary = cmp.bool(a.active, b.active); break;
      }
      if (primary !== 0) return primary * factor;
      return cmp.text(a.code, b.code);
    });
  }, [data, sort]);

  function refresh() {
    qc.invalidateQueries({ queryKey: ['parties'] });
  }

  return (
    <>
      <PageHeader title="Contrapartes" breadcrumb="Cadastros"
        actions={<Button size="sm" onClick={() => setOpenNew(true)}>+ Nova</Button>}
      />
      <PageBody>
        <div className="bg-card border rounded-lg p-3 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Tipo</Label>
            <Select value={filter.type} onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}>
              <option value="">Todos</option>
              <option value="CUSTOMER">Clientes</option>
              <option value="SUPPLIER">Fornecedores</option>
              <option value="EMPLOYEE">Funcionários</option>
              <option value="CONTRACTOR">Terceirizados</option>
            </Select>
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs">Buscar</Label>
            <Input placeholder="Nome..." value={filter.q} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} />
          </div>
        </div>

        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <SortHeader column="code"   label="Código"   sort={sort} onClick={toggleSort} />
                <SortHeader column="name"   label="Nome"     sort={sort} onClick={toggleSort} />
                <SortHeader column="type"   label="Tipo"     sort={sort} onClick={toggleSort} />
                <SortHeader column="email"  label="E-mail"   sort={sort} onClick={toggleSort} />
                <SortHeader column="phone"  label="Telefone" sort={sort} onClick={toggleSort} />
                <SortHeader column="active" label="Status"   sort={sort} onClick={toggleSort} />
                <th className="px-3 py-2 font-medium w-20 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Carregando…</td></tr>}
              {sortedItems.map((p) => (
                <tr key={p.id} className="group">
                  <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                  <td className="px-3 py-2">{p.name}</td>
                  <td className="px-3 py-2"><TypeBadge type={p.type} /></td>
                  <td className="px-3 py-2 text-muted-foreground">{p.email ?? '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">{p.phone ?? '—'}</td>
                  <td className="px-3 py-2">
                    {p.active ? <Badge variant="success">Ativo</Badge> : <Badge variant="danger">Inativo</Badge>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setEditing(p)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                        title="Editar"
                        aria-label="Editar contraparte"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setDeleting(p)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                        title="Excluir"
                        aria-label="Excluir contraparte"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && (data?.length ?? 0) === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Nenhuma contraparte</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>

      {openNew && (
        <PartyFormDialog
          editing={null}
          onClose={() => setOpenNew(false)}
          onSaved={() => { setOpenNew(false); refresh(); }}
        />
      )}
      {editing && (
        <PartyFormDialog
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {deleting && (
        <DeletePartyDialog
          target={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); refresh(); }}
        />
      )}
    </>
  );
}

// ===================== Form (criar / editar) =====================

function PartyFormDialog({
  editing,
  onClose,
  onSaved,
}: {
  editing: Party | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!editing;
  const [name, setName] = useState(editing?.name ?? '');
  const [type, setType] = useState<PartyType>((editing?.type as PartyType) ?? 'CUSTOMER');
  const [email, setEmail] = useState(editing?.email ?? '');
  const [phone, setPhone] = useState(editing?.phone ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [active, setActive] = useState(editing?.active ?? true);

  const create = useMutation({
    mutationFn: () => partiesApi.create({
      name,
      type,
      email: email || undefined,
      phone: phone || undefined,
      notes: notes || undefined,
    }),
    onSuccess: onSaved,
  });
  const update = useMutation({
    mutationFn: () => partiesApi.update(editing!.id, {
      name,
      type,
      email: email || undefined,
      phone: phone || undefined,
      notes: notes || undefined,
      active,
    }),
    onSuccess: onSaved,
  });

  const mut = isEdit ? update : create;
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    mut.mutate();
  }

  return (
    <Dialog open onClose={onClose} title={isEdit ? `Editar contraparte (${editing!.code})` : 'Nova contraparte'}>
      <form onSubmit={handleSubmit}>
        <DialogBody className="space-y-3">
          <div className="space-y-1">
            <Label>Nome</Label>
            <Input autoFocus maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Tipo</Label>
            <Select value={type} onChange={(e) => setType(e.target.value as PartyType)}>
              <option value="CUSTOMER">Clientes</option>
              <option value="SUPPLIER">Fornecedores</option>
              <option value="EMPLOYEE">Funcionários</option>
              <option value="CONTRACTOR">Terceirizados</option>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>E-mail</Label>
              <Input type="email" maxLength={120} value={email ?? ''} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Telefone</Label>
              <Input maxLength={40} value={phone ?? ''} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Observações</Label>
            <Input value={notes ?? ''} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-input"
              />
              Ativo
            </label>
          )}
          {!isEdit && (
            <div className="text-xs text-muted-foreground">
              Código será gerado automaticamente em sequência única (a partir de 001).
            </div>
          )}
          {apiError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
              {apiError}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={!name.trim() || mut.isPending}>
            {mut.isPending ? 'Salvando…' : isEdit ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// ===================== Excluir (com check de uso) =====================

function DeletePartyDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: Party;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { data: usage, isLoading: loadingUsage } = useQuery<PartyUsage>({
    queryKey: ['party-usage', target.id],
    queryFn: () => partiesApi.usage(target.id),
  });

  const remove = useMutation({
    mutationFn: () => partiesApi.remove(target.id),
    onSuccess: onDeleted,
  });
  const apiError = remove.error instanceof ApiError ? remove.error.message : null;

  const total = usage?.total ?? 0;
  const inUse = total > 0;

  return (
    <Dialog open onClose={onClose} title="Excluir contraparte">
      <DialogBody className="space-y-4">
        <p>
          Excluir a contraparte <strong>{target.name}</strong>{' '}
          <span className="font-mono text-xs text-muted-foreground">({target.code})</span>?
        </p>

        {loadingUsage && <p className="text-sm text-muted-foreground">Verificando uso…</p>}

        {!loadingUsage && !inUse && (
          <p className="text-sm text-muted-foreground">
            Nenhum lançamento usa esta contraparte. A exclusão é segura e definitiva.
          </p>
        )}

        {!loadingUsage && inUse && usage && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2">
            <p className="font-medium">
              Não é possível excluir — em uso em {total} registro(s):
            </p>
            <ul className="list-disc list-inside text-muted-foreground">
              {usage.transactions > 0 && <li>{usage.transactions} lançamento(s) em Caixa</li>}
              {usage.payables > 0 && <li>{usage.payables} conta(s) a pagar</li>}
              {usage.receivables > 0 && <li>{usage.receivables} conta(s) a receber</li>}
              {usage.recurringRules > 0 && <li>{usage.recurringRules} regra(s) recorrente(s)</li>}
            </ul>
            <p className="text-xs">
              Remova ou reatribua os registros vinculados antes de excluir esta contraparte.
              Como alternativa, edite-a e desmarque <strong>Ativo</strong> para ocultá-la
              dos seletores sem perder o histórico.
            </p>
          </div>
        )}

        {apiError && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
            {apiError}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {inUse ? 'Fechar' : 'Cancelar'}
        </Button>
        <Button
          type="button"
          variant="destructive"
          disabled={loadingUsage || inUse || remove.isPending}
          onClick={() => remove.mutate()}
        >
          {remove.isPending ? 'Excluindo…' : 'Excluir'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
